import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Ho4ArticleSidebar from '../components/Ho4ArticleSidebar';
import { HO4_LAST_MODIFIED } from '../data/ho4Articles';

function Ho4FloodDamageArticlePage() {
  const navigate = useNavigate();
  const articleLinkStyle = { color: '#065F46', fontWeight: 600, textDecoration: 'underline' };

  useEffect(() => {
    const previousTitle = document.title;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute('content') ?? '';

    document.title = 'Does Renters Insurance Cover Flood Damage in Texas? | GetSkimp';

    if (descriptionMeta) {
      descriptionMeta.setAttribute(
        'content',
        'Standard HO-4 renters insurance excludes flood damage. Learn what counts as a flood in Texas, what renters insurance does cover, and your options.'
      );
    }

    return () => {
      document.title = previousTitle;
      if (descriptionMeta) descriptionMeta.setAttribute('content', previousDescription);
    };
  }, []);

  return (
    <div className="container">
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '2rem' }}>
        <Ho4ArticleSidebar
          activeHref="/knowledge-base/does-renters-insurance-cover-flood-damage"
          onNavigate={(href) => navigate(href)}
        />
        <section>
          <div style={{ marginBottom: '1rem' }}>
            <button type="button" className="btn btn-sm btn-outline" onClick={() => navigate('/knowledge-base')} style={{ marginBottom: '1rem' }}>
              Back to self help library
            </button>
            <h2 style={{ marginBottom: '0.5rem' }}>Does renters insurance cover flood damage in Texas?</h2>
            <p style={{ marginBottom: 0, color: '#4B5563' }}>
              Texas sees hurricanes, tropical storms, and flash flooding — but standard HO-4 policies treat flood differently from burst pipes or storm wind.
            </p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6B7280' }}>Last modified: {HO4_LAST_MODIFIED}</p>
          </div>

          <article className="card mb-4">
            <div className="card-body">
              <h3 style={{ marginBottom: '0.75rem' }}>Quick answer</h3>
              <p>
                <strong>No</strong> — standard renters insurance (HO-4) does not cover flood damage to your belongings.
                Flood is defined as water that rises from the ground or overflows a body of water — not the same as a burst
                pipe or appliance leak. Texas renters in flood-prone areas should consider separate flood insurance for
                personal property.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Flood vs. water damage — what is covered?</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Event</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>HO-4 usually covers?</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Hurricane storm surge / river overflow</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>No — flood exclusion</td>
                    </tr>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Flash flooding from heavy rain (groundwater)</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>No — flood exclusion</td>
                    </tr>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Burst pipe or appliance leak</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Often yes — see{' '}
                        <Link to="/knowledge-base/does-ho4-cover-water-damage" style={articleLinkStyle}>water damage guide</Link>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Rain through a roof hole after wind damage</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Sometimes — depends on how the policy defines the peril</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '0.6rem' }}>Sewer backup</td>
                      <td style={{ padding: '0.6rem' }}>Usually no — may need a separate endorsement</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Flood insurance options for Texas renters</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>NFIP contents coverage</strong> — available through FEMA&apos;s National Flood Insurance Program for renters; covers personal belongings in participating communities.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Private flood insurance</strong> — some carriers offer renters flood policies with different limits and waiting periods.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  You do not need to own the building — renters can buy contents-only flood coverage.
                </li>
                <li>
                  There is typically a 30-day waiting period before NFIP coverage takes effect — do not wait until a storm is forecast.
                </li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>What to do before and after a flood</h3>
              <ol style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>Check FEMA flood maps for your address — many Texas apartments sit in flood zones.</li>
                <li style={{ marginBottom: '0.5rem' }}>Keep belongings elevated in ground-floor units when possible.</li>
                <li style={{ marginBottom: '0.5rem' }}>Document belongings with photos and receipts for any future claim.</li>
                <li style={{ marginBottom: '0.5rem' }}>After flooding, contact your landlord and document damage before cleanup.</li>
                <li>
                  If you have flood contents coverage, file promptly and keep all damaged-item evidence.
                </li>
              </ol>

              <p style={{ marginTop: '1rem', marginBottom: 0 }}>
                HO-4 still protects you against many other perils — see{' '}
                <Link to="/knowledge-base/what-is-ho4-renters-insurance" style={articleLinkStyle}>what HO-4 covers</Link>{' '}
                and{' '}
                <Link to="/knowledge-base/is-ho4-worth-it" style={articleLinkStyle}>is HO-4 worth it</Link>{' '}
                for the full picture.
              </p>
            </div>
          </article>

          <section className="card mb-4" style={{ borderColor: 'var(--color-green)', background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)', boxShadow: 'var(--shadow-md)' }}>
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#065F46' }}>Start renters insurance with GetSkimp</h3>
              <p style={{ marginBottom: '1rem' }}>Get HO-4 coverage for theft, fire, liability, and sudden water damage — then ask about flood options for your area.</p>
              <button type="button" className="btn btn-primary" onClick={() => navigate('/qualify')} style={{ fontWeight: 700, paddingInline: '1.4rem' }}>Get started now</button>
            </div>
          </section>
          <section className="card" style={{ borderColor: '#FBBF24', background: 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)' }}>
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#92400E' }}>In a flood zone or near the coast?</h3>
              <p style={{ marginBottom: '1rem' }}>An agent can explain flood contents coverage and how it works alongside your HO-4 policy.</p>
              <button type="button" className="btn btn-outline" onClick={() => navigate('/help')} style={{ borderColor: '#D97706', color: '#92400E', fontWeight: 700 }}>Speak to an agent</button>
            </div>
          </section>
        </section>
      </div>
    </div>
  );
}

export default Ho4FloodDamageArticlePage;
